import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResultAnswers } from 'store/player/playerAsync';
import { selectResult } from 'store/player/playerSelectors';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
import PageTypes from 'types/PageTypes';
// MUI
import { Box } from '@mui/material';
// Components
import AppLoader from 'components/AppLoader';
import QRCode from './QRCode';
import AnswerList from './AnswersList';
// i18next
import i18n from 'i18n'

let interval: any;
let questionInterval: any;

const LiveResultsPage:FC = () => {
  const { slug } = useParams<{ slug:string }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [progress, setProgress] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.matchMedia("(min-width: 640px)").matches);

  const mediaHandler = (e:any) => setIsLargeScreen(e.matches);

  useEffect(() => {
    window.matchMedia("(min-width: 640px)").addEventListener('change', mediaHandler);

    return () => {
      window.matchMedia("(min-width: 640px)").removeEventListener('change', mediaHandler);
    }
  }, []);

  const result:any = useAppSelector(selectResult);
  const quiz:IQuiz | null = result?.quiz || null;
  const questionsRefs = quiz?.pages?.filter(page => page.type === PageTypes.Question).map(page => page.refs[0]);
  const question = quiz?.questions.filter(q => questionsRefs?.includes(q.ref))[questionIndex];
  const results = result?.results || [];
  const questionResults = results.find((r:any) => r.questionRef === question?.ref);

  const questionAnswers = [...(question?.answers || [])].sort((a, b) => {
    const aCount = questionResults?.answers[a.ref] || 0;
    const bCount = questionResults?.answers[b.ref] || 0;
    return bCount - aCount;
  });

  const allAnswers:number[] = [];

  Object.keys(questionResults?.answers || {}).forEach((key) => {
    if (question?.answers.find(answer => answer.ref === key)) {
      allAnswers.push(questionResults.answers[key] || 0)
    }
  })

  const sum = allAnswers.reduce((a,b) => a+b, 0);

  const fetchResult = (isTimer: boolean) => {
    dispatch(getResultAnswers({ quizName: slug })).unwrap()
      .then(() => {
        if (!isTimer) {
          setDataLoaded(true);
        }
      })
      .catch(() => {
        if (!isTimer) {
          navigate('/404-not-found');
        }
      })
      .finally(() => setLoading(false));
  }

  const handleChangeQuestion = useCallback(() => {
    if (!questionsRefs) return;
    setProgress(0);
    setQuestionIndex(prev => {
      if (prev + 1 >= questionsRefs.length) return 0;
      return prev + 1;
    })
  }, [questionsRefs]);

  useEffect(() => {
    setLoading(true);
    fetchResult(false);

    interval = setInterval(() => { fetchResult(true) }, 10000);

    const rootDiv = document.getElementById('root');
    rootDiv?.classList.add('liveLeaderboardMode');

    return () => {
      clearInterval(interval);
      rootDiv?.classList.remove('liveLeaderboardMode');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!quiz) return;
    if (![QuizTypes.Poll].includes(quiz.type)) navigate('/404-not-found');
    i18n.changeLanguage(quiz.language || 'en');
    document.documentElement.style.setProperty('--primaryColor', quiz.theme.primaryColor);
    // eslint-disable-next-line
  }, [quiz]);

  useEffect(() => {
    if (!dataLoaded) return;
    questionInterval = setInterval(() => { handleChangeQuestion() }, (quiz?.publicLeaderboard.questionsTimeout || 5) * 1000);

    return () => {
      clearInterval(questionInterval);
    }
    // eslint-disable-next-line
  }, [dataLoaded]);

  useEffect(() => {
    if (!dataLoaded) return;
    const step = 100 / ((quiz?.publicLeaderboard.questionsTimeout || 5) * 10);
    const progressInterval = setInterval(() => {
      setProgress(prev => (prev + step > 100 ? 100 : prev + step));
    }, 100);
  
    return () => clearInterval(progressInterval);
    // eslint-disable-next-line
  }, [dataLoaded, questionIndex]);

  const bgImageUrl = quiz ? quiz.publicLeaderboard.wallpaperImage : '';
  const portraitBgImageUrl = quiz ? quiz.publicLeaderboard.portraitWallpaperImage : '';
  
  if (loading || !result || !quiz) return <AppLoader />;
  return (
    <Box
      className="
        h-full overflow-scroll p-4
        flex flex-col landscape:justify-between portrait:gap-8
        bg-cover bg-center 
        sm:p-8 xl:bg-right-top
      "
      sx={{
        color: quiz?.publicLeaderboard.textColor,
        '@media screen and (orientation: portrait)': {
          backgroundImage: `url(${portraitBgImageUrl || bgImageUrl})`,
        },

        '@media screen and (orientation: landscape)': {
          backgroundImage: `url(${bgImageUrl || portraitBgImageUrl})`,
        }
      }}
    >
      <div>
        <div className="flex justify-between">
          <img
            src={quiz.logoImage} alt={quiz.quizName}
            className="h-12 sm:h-24 object-contain shadow-primary rounded-lg"
            style={{ backgroundColor: quiz.logoImageBackgroundColor }}
          />
          {quiz.sponsorImage ? (
            <img
              src={quiz.sponsorImage} alt="sponsor"
              className="h-12 sm:h-24 object-contain shadow-primary rounded-lg"
              style={{ backgroundColor: quiz.sponsorImageBackgroundColor }}
            />
          ) : null}
        </div>
        <div className="w-full landscape:w-2/3 py-8 text-center">
          <h1 className="py-8 text-2xl sm:text-8xl font-extrabold border-b border-white uppercase">{quiz.publicLeaderboard.headerText || quiz.quizName}</h1>
          <h1 className="py-8 text-2xl sm:text-7xl font-extrabold border-b border-white">{question?.title}</h1>
        </div>
      </div>
      <div className="flex justify-between gap-8 portrait:flex-col">
        <div className="w-full landscape:w-2/3">
          <AnswerList questionResults={questionResults} questionAnswers={questionAnswers} allAnswers={sum} />
          {questionsRefs && questionsRefs.length > 1 ? (
            <div className="mt-4 p-2 rounded-full shadow-primary-border">
              <div
                className="h-6 rounded-full"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(to bottom, rgb(232 238 143), rgb(106 210 107))',
                  transition: 'width 0.1s',
                }}
              />
            </div>
          ) : null}
        </div>
        <QRCode isLargeScreen={isLargeScreen} />
      </div>
    </Box>
  );
}

export default LiveResultsPage;
